import { motion } from 'framer-motion';
import { Zap, Eye, Bug } from 'lucide-react';

export default function LoginHero() {
  return (
    <div className="relative w-full h-full flex flex-col justify-center px-2">
      {/* Subtle noise texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Hero Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center space-y-3 mb-8"
      >
        <h1 className="text-3xl md:text-4xl font-bold leading-tight">
          <span className="text-gray-100">Debug webhooks,</span>
          <br />
          <span className="bg-gradient-to-r from-[#B497FF] via-[#9F85FF] to-[#7AB7FF] bg-clip-text text-transparent">no friction</span>
        </h1>
        <p className="text-sm text-gray-400 max-w-xs mx-auto leading-relaxed">
          Get instant webhook endpoints. See every request. Debug issues fast.
        </p>
      </motion.div>

      {/* Feature Cards */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.6 }}
        className="grid gap-4 max-w-md mx-auto w-full"
      >
        {/* Card 1 - Instant Endpoints */}
        <motion.div
          whileHover={{ y: -2 }}
          transition={{ duration: 0.2 }}
          className="group p-4 rounded-lg
            bg-[#151515]
            border border-white/[0.06]
            hover:border-white/[0.1]
            transition-all duration-200"
        >
          <div className="flex items-start gap-3.5">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 bg-green-500/8 rounded-lg flex items-center justify-center border border-green-500/15">
                <Zap className="h-5 w-5 text-green-400" strokeWidth={2} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-sm text-gray-100 mb-1">Instant Endpoints</h3>
              <p className="text-xs text-gray-500 leading-relaxed">Get a webhook URL instantly. No signup needed.</p>
            </div>
          </div>
        </motion.div>

        {/* Card 2 - Live Inspection */}
        <motion.div
          whileHover={{ y: -2 }}
          transition={{ duration: 0.2 }}
          className="group p-4 rounded-lg
            bg-[#151515]
            border border-white/[0.06]
            hover:border-white/[0.1]
            transition-all duration-200"
        >
          <div className="flex items-start gap-3.5">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 bg-blue-500/8 rounded-lg flex items-center justify-center border border-blue-500/15">
                <Eye className="h-5 w-5 text-blue-400" strokeWidth={2} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-sm text-gray-100 mb-1">Live Inspection</h3>
              <p className="text-xs text-gray-500 leading-relaxed">See requests in real-time. Inspect headers, payloads, responses.</p>
            </div>
          </div>
        </motion.div>

        {/* Card 3 - Easy Debugging */}
        <motion.div
          whileHover={{ y: -2 }}
          transition={{ duration: 0.2 }}
          className="group p-4 rounded-lg
            bg-[#151515]
            border border-white/[0.06]
            hover:border-white/[0.1]
            transition-all duration-200"
        >
          <div className="flex items-start gap-3.5">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 bg-purple-500/8 rounded-lg flex items-center justify-center border border-purple-500/15">
                <Bug className="h-5 w-5 text-purple-400" strokeWidth={2} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-sm text-gray-100 mb-1">Easy Debugging</h3>
              <p className="text-xs text-gray-500 leading-relaxed">Beautiful formatting. Find issues fast with detailed logs.</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
